import React, {
  useContext, useReducer, useEffect, useRef
} from 'react';
import PropTypes from 'prop-types';
import { Select } from '@glu/form-components';
import locale from '@glu/locale';
import { withStyles, withTheme } from '@glu/theming';
import SchedulerContext from '../SchedulerContext';
import reducer, { types } from './TimePicker.reducer';
import StyledSelect from './StyledSelect';
import { getHourOptions, get24HourOptions } from '../utils/recurrence';
import { formatTime, getTime } from '../utils/time';

const styles = {
  timePicker: {},
  meridiemSelect: {
    display: 'inline-block',
    width: 70,
    marginLeft: 10
  },
  separator: {
    display: 'inline-block',
    width: 5
  }
};

const minuteOptions = Array(60).fill().map((_, idx) => ({
  label: idx < 10 ? `0${idx}` : idx,
  value: idx
}));

const meridiemOptions = [{
  label: 'AM',
  value: 'AM'
}, {
  label: 'PM',
  value: 'PM'
}];

const TimePicker = ({
  classes, className, htmlId, screenReaderLabel, minTime, maxTime, value, onChange, hideMinutes
}) => {
  const {
    disabled, timePicker24Hour
  } = useContext(SchedulerContext);

  const [time, dispatch] = useReducer(reducer, getTime(value, timePicker24Hour));
  const prevTime = useRef();
  const localScreenReaderLabel = true;

  useEffect(() => {
    if (prevTime.current && (time.hours !== prevTime.current.hours
      || time.minutes !== prevTime.current.minutes
      || time.meridiem !== prevTime.current.meridiem)) {
      onChange({ ...time, value: formatTime(time, timePicker24Hour) });
    }
    prevTime.current = time;
  }, [time, onChange, timePicker24Hour]);

  // eslint doesn't recognize selects as valid inputs for labels, from glu-form-components
  return (
    <div className={`${className} ${classes.timePicker}`}>
      <StyledSelect
        htmlId={`${htmlId}-hourSelect`}
        name={`${htmlId}-hourSelect`}
        dataQa="hour-select"
        labelText={locale.get('schedulerReact.atTime')}
        screenReaderLabel={screenReaderLabel}
        value={parseInt(time.hours, 10)}
        className={classes.hourSelect}
        options={timePicker24Hour
          ? get24HourOptions(minTime, maxTime)
          : getHourOptions(time.meridiem, minTime, maxTime)}
        disabled={disabled}
        onChange={(e) => dispatch({ type: types.SET_HOURS, response: e.target.value })}
      />
      {!hideMinutes && (
        <>
          <span className={classes.separator}>:</span>
          <StyledSelect
            htmlId={`${htmlId}-minuteSelect`}
            name={`${htmlId}-minuteSelect`}
            dataQa="minute-select"
            value={parseInt(time.minutes, 10)}
            className={classes.minuteSelect}
            labelText={locale.get('schedulerReact.minutes')}
            screenReaderLabel={localScreenReaderLabel}
            options={minuteOptions}
            disabled={disabled}
            onChange={(e) => dispatch({ type: types.SET_MINUTES, response: e.target.value })}
          />
        </>
      )}
      {!timePicker24Hour && (
        <Select
          htmlId={`${htmlId}-meridiemSelect`}
          name={`${htmlId}-meridiemSelect`}
          data-qa="meridiem-select"
          className={classes.meridiemSelect}
          labelText={locale.get('schedulerReact.on')}
          screenReaderLabel={localScreenReaderLabel}
          options={meridiemOptions}
          disabled={disabled}
          value={time.meridiem}
          disableInputAPI
          onChange={(e) => {
            dispatch({
              type: types.SET_MERIDIEM,
              response: {
                meridiem: e.target.value,
                minTime,
                maxTime
              }
            });
          }}
        />
      )}
    </div>
  );
};

TimePicker.propTypes = {
  /** Classes provided by the theme */
  classes: PropTypes.objectOf(PropTypes.string).isRequired,

  /** Class name provided by consuming component */
  className: PropTypes.string,

  /** String assigned to HTML id attribute of component */
  htmlId: PropTypes.string.isRequired,

  /** Minimum time value of input */
  minTime: PropTypes.string,

  /** Maximum time value of input */
  maxTime: PropTypes.string,

  /** Hides minute controls, only displaying hours to control * */
  hideMinutes: PropTypes.bool,

  /** Value of input */
  value: PropTypes.string,

  /** Display label only to screenreaders */
  screenReaderLabel: PropTypes.bool,

  /** Callback to set recurrence value */
  onChange: PropTypes.func.isRequired
};

TimePicker.defaultProps = {
  className: '',
  minTime: undefined,
  maxTime: undefined,
  hideMinutes: false,
  value: '12:00 PM',
  screenReaderLabel: false
};

export const BareTimePicker = TimePicker;
export default withTheme(withStyles(styles)(TimePicker));
